import SearchPrediction, {
  createSearchPredictionModel,
} from 'data/search-predictions';
import { queryResidentialPlaces } from './api/residentialPlaces';
import { queryCommercialPlaces } from './api/commercialPlaces';
import { AutocompletePredictionPredictionCategory, SearchPredictionSource } from './types';
import { getPredictionCategory } from './predictionClassifier';
import { CountryCode, CountryCodeList } from 'types/countries';

export const US_AND_CANADA_COUNTRY_FILTER: readonly CountryCode[] = [CountryCodeList.CANADA, CountryCodeList.UNITED_STATES];
export const CANADA_COUNTRY_FILTER: readonly CountryCode[] = [CountryCodeList.CANADA];

/**
 * Residential search: city, neighbourhood and street predictions from Google Maps.
 * Provinces/states come from go-search instead.
 *
 * @param input - The search query string
 * @param useUsListings - Whether to include US listings (affects country filtering)
 * @param country - The country code to search within
 */
export async function getResidentialGooglePredictions(
  input: string,
  useUsListings: boolean,
  country: CountryCode
): Promise<SearchPrediction[]> {
  const countryFilter: readonly CountryCode[] = useUsListings ? US_AND_CANADA_COUNTRY_FILTER : [country];
  const { city, neighborhood, street } = await queryResidentialPlaces(input, countryFilter);
  return toSearchPredictions([city, neighborhood, street]);
}

/**
 * eXp Commercial search: US city, street, state and ZIP code predictions from Google Maps.
 */
export async function getCommercialGooglePredictions(input: string): Promise<SearchPrediction[]> {
  const { city, neighborhood, street, province, postalCode } = await queryCommercialPlaces(input);
  return toSearchPredictions([city, neighborhood, street, province, postalCode]);
}

function toSearchPredictions(groups: google.maps.places.AutocompletePrediction[][]): SearchPrediction[] {
  return groups
    .flatMap(group => autocompletePredictionToSearchResults(group))
    .map(prediction => createSearchPredictionModel(prediction as SearchPrediction));
}

export function autocompletePredictionToSearchResults(autocompletePrediction: google.maps.places.AutocompletePrediction[]) {
  return autocompletePrediction.map(prediction => {
    let label;
    const predictionCategory = getPredictionCategory(prediction?.types);
    switch (predictionCategory) {
      case AutocompletePredictionPredictionCategory.CITY:
        label = 'city';
        break;
      case AutocompletePredictionPredictionCategory.NEIGHBORHOOD:
        label = 'neighbourhood';
        break;
      case AutocompletePredictionPredictionCategory.STREET:
        label = 'street';
        break;
      // right now expCommercial uses province & postal code predictions from google maps
      case AutocompletePredictionPredictionCategory.PROVINCE:
        label = 'state';
        break;
      case AutocompletePredictionPredictionCategory.POSTAL_CODE:
        label = 'zip';
        break;
      default:
        label = 'city';
    }

    return {
      id: prediction.place_id,
      description: prediction.description.replace(', Canada', '').replace(', USA', ''),
      group: 'locations',
      matchedSubstrings: prediction.matched_substrings,
      data: null,
      label,
      source: SearchPredictionSource.GOOGLE,
    };
  });
}
